import { Link, useNavigate } from 'react-router'
import { useTranslation } from 'react-i18next'
import { Card, CardBody, CompetitionPoster, PosterImage, Spinner } from '@structural-climbing/ui'
import { useGymContext } from '../../contexts/GymContext'
import { PageHeader } from '../../components/PageHeader'
import { competitionState, gymLocalNow, type Competition, type CompetitionState } from './types'

interface Props {
  competitions: Competition[]
  loading: boolean
}

const STATE_STYLES: Record<CompetitionState, string> = {
  in_progress: 'bg-success/15 text-success-700',
  upcoming: 'bg-primary/15 text-primary-700',
  ended: 'bg-default/40 text-foreground/60',
}

export function CompetitionsSection({ competitions, loading }: Props) {
  const { t } = useTranslation()
  const { slug, timezone } = useGymContext()
  const navigate = useNavigate()

  const localNow = gymLocalNow(timezone)

  // A full page now: the seven setup steps no longer fit a modal.
  const openCreate = () => navigate(`/${slug}/competitions/new`)

  return (
    <>
      {/* "New competition" sits in the header, like every other page's main action. */}
      <PageHeader
        title={t('competitions.title')}
        subtitle={t('competitions.subtitle')}
        primaryAction={{ label: t('competitions.create'), onPress: openCreate }}
      />
      {loading ? (
        <div className="flex justify-center py-8">
          <Spinner />
        </div>
      ) : competitions.length === 0 ? (
        <Card shadow="sm">
          <CardBody>
            <p className="py-8 text-center text-foreground/60">{t('competitions.empty')}</p>
          </CardBody>
        </Card>
      ) : (
        <ul className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {competitions.map((competition) => {
            const state = competitionState(competition, localNow)
            return (
              <li key={competition.id}>
                <Card
                  as={Link}
                  to={`/${slug}/competitions/${competition.id}`}
                  shadow="sm"
                  className="flex h-full w-full flex-col gap-3 p-3 text-left transition-shadow hover:shadow-md"
                >
                  {/* The same poster the member and public lists show. */}
                  <div className="aspect-[2/1] w-full overflow-hidden rounded-xl">
                    {competition.poster_url ? (
                      <PosterImage src={competition.poster_url} />
                    ) : (
                      <CompetitionPoster name={competition.name} />
                    )}
                  </div>
                  <div className="flex min-w-0 flex-1 flex-col gap-1 px-1 pb-1">
                    <div className="flex items-start gap-2">
                      <span className="min-w-0 flex-1 font-semibold break-words">
                        {competition.name}
                      </span>
                      <span
                        className={`shrink-0 rounded-full px-2 py-0.5 text-xs font-medium ${STATE_STYLES[state]}`}
                      >
                        {t(`competitions.state_${state}`)}
                      </span>
                    </div>
                    {!competition.published && (
                      <span className="w-fit rounded-full bg-warning/15 px-2 py-0.5 text-xs font-medium text-warning-700">
                        {t('competitions.draft')}
                      </span>
                    )}
                    <p className="text-sm text-foreground/60">
                      {t('competitions.competitorCount', { count: competition.entry_count ?? 0 })}
                      {' · '}
                      {t('competitions.routeCount', { count: competition.route_count ?? 0 })}
                    </p>
                    <p className="text-xs text-foreground/45">
                      {t(`competitions.discipline_${competition.discipline}`)} ·{' '}
                      {t(`competitions.format_${competition.format}`)}
                    </p>
                  </div>
                </Card>
              </li>
            )
          })}
        </ul>
      )}
    </>
  )
}
